import WasmController from "react-lib/frameworks/WasmController";

import DivisionAccessGrantAdminListCreateView from "issara-sdk/apis/DivisionAccessGrantAdminListCreateView_core";
import DivisionAccessGrantAdminActionView from "issara-sdk/apis/DivisionAccessGrantAdminActionView_core";

import { State as MainState } from "HIS/MainHISInterface";
import { SetProperty } from "react-lib/apps/HISV3/common/CommonInterface";

export type State = Partial<{
  // sequence
  WellnessOAAccessGrantSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    filterWellnessOA: FilterWellnessOAType;
    ownerAccessList: AccessListType;
    granteerAccessList: AccessListType;
    actionCreate: ActionCreateType;
    actionReject: Record<string, any>;
    actionCancel: Record<string, any>;
  }> | null;
  selectedDivision?: Record<string, any>;
}>;

type ActionCreateType = {
  owner_division?: number | string;
  access_type?: string[];
};

type FilterWellnessOAType = {
  status?: string;
};

type AccessListType = {
  items?: Record<string, any>[];
  activePage?: number;
  total?: number;
};

type Picked = Partial<
  Pick<MainState, "buttonLoadCheck" | "errorMessage" | "django" | "searchedItemListWithKey">
>;

// Sequence
type SeqState = {
  sequence: "WellnessOAAccessGrant";
  restart?: boolean;
  clear?: boolean;
  card?: string;
};

// Handle Action
type ActionType =
  | {
      action: "SEARCH";
      card?: string;
      ownerActivePage?: number;
      granteeActivePage?: number;
      status?: string;
    }
  | { action: "CREATE_ACCESS"; card: string; onCloes?: any }
  | { action: "REJECT_ACCESS"; card: string; onCloes?: any }
  | { action: "CANCEL_ACCESS"; card: string; onCloes?: any }
  | { action: "CONFIRM_ACCESS"; card: string; item?: any };

type SeqAct = ActionType & SeqState;
type SeqType<K> = K extends { action: string } ? Extract<SeqAct, K> : SeqState;

export type RunSequence = <K extends keyof SeqAct>(params: SeqType<Pick<SeqAct, K>>) => any;

export type SetProp = SetProperty<State & Picked>;

type CustomExtract<T, U> = T extends T ? (U extends Partial<T> ? T : never) : never;

type Params<A extends ActionType["action"]> = CustomExtract<ActionType, { action: A }>;

export const DEFAULT_LIST_LIMIT = 10;

export const StateInitial: State = {
  // sequence
  WellnessOAAccessGrantSequence: {
    sequenceIndex: null,
  },
};

export type Event = { message: "RunSequence"; params: {} };

export type Data = {
  division?: number;
  device?: number;
};

export const BUTTON_ACTIONS = {
  SEARCH: "SEARCH",
  CREATE_ACCESS: "CREATE_ACCESS",
  REJECT_ACCESS: "REJECT_ACCESS",
  CANCEL_ACCESS: "CANCEL_ACCESS",
  CONFIRM_ACCESS: "CONFIRM_ACCESS",
} as const;

type Handler<P = any, R = any> = (
  controller: WasmController<State & Picked, Event, Data, Props>,
  params: P,
) => R;

const Masters = [["division", {}]] as const;

/* ------------------------------------------------------ */

/*                          START                         */

/* ------------------------------------------------------ */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({
    message: "GetMasterData",
    params: {
      masters: Masters,
    },
  } as any);

  controller.setState(
    {
      WellnessOAAccessGrantSequence: {
        ...state.WellnessOAAccessGrantSequence,
        sequenceIndex: "Action",
        filterWellnessOA: {
          status: "ALL",
        },
      },
    },
    () => {
      Action(controller, {
        action: "SEARCH",
        ...params,
        ownerActivePage: 1,
        granteeActivePage: 1,
      });
    },
  );
};

/* ------------------------------------------------------ */

/*                      Handle Action                     */

/* ------------------------------------------------------ */
export const Action: Handler<ActionType> = async (controller, params) => {
  if (params.action === "SEARCH") {
    HandleSearch(controller, params);
  } else if (params.action === "CREATE_ACCESS") {
    HandleCreateAccess(controller, params);
  } else if (params.action === "REJECT_ACCESS") {
    HandleRejectAccess(controller, params);
  } else if (params.action === "CANCEL_ACCESS") {
    HandleCancelAccess(controller, params);
  } else if (params.action === "CONFIRM_ACCESS") {
    HandleConfirmAccess(controller, params);
  }
};

const HandleSearch: Handler<Params<"SEARCH">> = async (controller, params) => {
  const state = controller.getState();
  const currentFilter = state.WellnessOAAccessGrantSequence?.filterWellnessOA;
  const buttonKey = `${params.card}_${params.action}`;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [buttonKey]: "LOADING",
    },
  });

  const ownerActivePage = params.ownerActivePage || 1;
  const granteeActivePage = params.granteeActivePage || 1;
  const status = params.status ?? currentFilter?.status ?? "ALL";

  const searchParams = {
    limit: DEFAULT_LIST_LIMIT,
    ...(status !== "ALL" && { status }),
  };

  const [ownerResult, granteeResult] = await Promise.all([
    DivisionAccessGrantAdminListCreateView.list({
      params: {
        ...searchParams,
        owner: state.selectedDivision?.id,
        offset: (ownerActivePage - 1) * DEFAULT_LIST_LIMIT,
      },
      apiToken: controller.apiToken,
      extra: { division: controller.data.division },
    }),
    DivisionAccessGrantAdminListCreateView.list({
      params: {
        ...searchParams,
        grantee: state.selectedDivision?.id,
        offset: (granteeActivePage - 1) * DEFAULT_LIST_LIMIT,
      },
      apiToken: controller.apiToken,
      extra: { division: controller.data.division },
    }),
  ]);

  const [ownerRes, ownerErr] = ownerResult;
  const [granteeRes, granteeErr] = granteeResult;

  const currentState = controller.getState();
  const hasError = !!ownerErr || !ownerRes || !!granteeErr || !granteeRes;

  controller.setState({
    buttonLoadCheck: {
      ...currentState.buttonLoadCheck,
      [buttonKey]: hasError ? "ERROR" : "SUCCESS",
    },
    WellnessOAAccessGrantSequence: {
      ...currentState.WellnessOAAccessGrantSequence,
      ...(ownerRes && {
        ownerAccessList: {
          ...currentState.WellnessOAAccessGrantSequence?.ownerAccessList,
          activePage: ownerActivePage,
          items: ownerRes.items || [],
          total: ownerRes.total || 0,
        },
      }),
      ...(granteeRes && {
        granteerAccessList: {
          ...currentState.WellnessOAAccessGrantSequence?.granteerAccessList,
          activePage: granteeActivePage,
          items: granteeRes.items || [],
          total: granteeRes.total || 0,
        },
      }),
      filterWellnessOA: {
        ...currentFilter,
        status: status,
      },
    },
  });
};

const HandleCreateAccess: Handler<Params<"CREATE_ACCESS">> = async (controller, params) => {
  const state = controller.getState();
  const buttonKey = `${params.card}_${params.action}`;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [buttonKey]: "LOADING",
    },
  });

  const actionCreate = state.WellnessOAAccessGrantSequence?.actionCreate;

  const accessTypes = actionCreate?.access_type || [];
  const ownerDivision = actionCreate?.owner_division;

  if (!ownerDivision) {
    controller.setState({
      buttonLoadCheck: {
        ...controller.getState().buttonLoadCheck,
        [buttonKey]: "ERROR",
      },
      errorMessage: {
        ...controller.getState().errorMessage,
        [params.card]: "กรุณาเลือกผู้ส่งคำขอ",
      },
    });
    return;
  }
  if (accessTypes.length === 0) {
    controller.setState({
      buttonLoadCheck: {
        ...controller.getState().buttonLoadCheck,
        [buttonKey]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [params.card]: "กรุณาเลือกสิทธิ์การเข้าถึง",
      },
    });

    return;
  }

  const results = await Promise.all(
    accessTypes.map((accessType) =>
      DivisionAccessGrantAdminListCreateView.create({
        data: {
          owner_division: actionCreate?.owner_division,
          access_type: accessType,
        },
        apiToken: controller.apiToken,
        extra: { division: controller.data.division },
      }),
    ),
  );

  const hasError = results.some(
    ([ownerAccessRes, ownerAccessErr]) => ownerAccessErr || !ownerAccessRes,
  );

  if (hasError) {
    controller.setState({
      buttonLoadCheck: {
        ...controller.getState().buttonLoadCheck,
        [buttonKey]: "ERROR",
      },
    });
  } else {
    HandleSearch(controller, {
      action: "SEARCH",
      card: params.card,
      ownerActivePage: 1,
      granteeActivePage: 1,
    });

    controller.setState({
      buttonLoadCheck: {
        ...controller.getState().buttonLoadCheck,
        [buttonKey]: "SUCCESS",
      },
    });

    params.onCloes();
  }
};

const HandleRejectAccess: Handler<Params<"REJECT_ACCESS">> = async (controller, params) => {
  const state = controller.getState();
  const buttonKey = `${params.card}_${params.action}`;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [buttonKey]: "LOADING",
    },
  });

  const actionCreate = state.WellnessOAAccessGrantSequence?.actionReject;
  const reason = actionCreate?.reason;

  if (!reason) {
    controller.setState({
      buttonLoadCheck: {
        ...controller.getState().buttonLoadCheck,
        [buttonKey]: "ERROR",
      },
      errorMessage: {
        ...controller.getState().errorMessage,
        [params.card]: "กรุณาระบุเหตุผลในการปฏิเสธหรือยกเลิกคำขอ",
      },
    });
    return;
  }

  const [actionAccessRes, actionAccessErr, actionAccessNet] =
    await DivisionAccessGrantAdminActionView.update({
      pk: state.WellnessOAAccessGrantSequence?.actionReject?.id,
      data: {
        action: "REJECT",
        reason: state.WellnessOAAccessGrantSequence?.actionReject?.reason,
      },
      apiToken: controller.apiToken,
      extra: { division: controller.data.division },
    });

  if (actionAccessErr) {
    controller.setState({
      buttonLoadCheck: {
        ...controller.getState().buttonLoadCheck,
        [buttonKey]: "ERROR",
      },
    });
  } else {
    HandleSearch(controller, {
      action: "SEARCH",
      card: params.card,
      ownerActivePage: 1,
      granteeActivePage: 1,
    });

    controller.setState({
      buttonLoadCheck: {
        ...controller.getState().buttonLoadCheck,
        [buttonKey]: "SUCCESS",
      },
    });

    params.onCloes();
  }
};

const HandleCancelAccess: Handler<Params<"CANCEL_ACCESS">> = async (controller, params) => {
  const state = controller.getState();
  const buttonKey = `${params.card}_${params.action}`;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [buttonKey]: "LOADING",
    },
  });

  const actionCancel = state.WellnessOAAccessGrantSequence?.actionCancel;
  const reason = actionCancel?.reason;

  if (!reason) {
    controller.setState({
      buttonLoadCheck: {
        ...controller.getState().buttonLoadCheck,
        [buttonKey]: "ERROR",
      },
      errorMessage: {
        ...controller.getState().errorMessage,
        [params.card]: "กรุณาระบุเหตุผลในการปฏิเสธหรือยกเลิกคำขอ",
      },
    });
    return;
  }

  const [actionAccessRes, actionAccessErr, actionAccessNet] =
    await DivisionAccessGrantAdminActionView.update({
      pk: state.WellnessOAAccessGrantSequence?.actionCancel?.id,
      data: {
        action: "CANCEL",
        reason: state.WellnessOAAccessGrantSequence?.actionCancel?.reason,
      },
      apiToken: controller.apiToken,
      extra: { division: controller.data.division },
    });

  if (actionAccessErr) {
    controller.setState({
      buttonLoadCheck: {
        ...controller.getState().buttonLoadCheck,
        [buttonKey]: "ERROR",
      },
    });
  } else {
    HandleSearch(controller, {
      action: "SEARCH",
      card: params.card,
      ownerActivePage: 1,
      granteeActivePage: 1,
    });

    controller.setState({
      buttonLoadCheck: {
        ...controller.getState().buttonLoadCheck,
        [buttonKey]: "SUCCESS",
      },
    });

    params.onCloes();
  }
};

const HandleConfirmAccess: Handler<Params<"CONFIRM_ACCESS">> = async (controller, params) => {
  const state = controller.getState();
  const buttonKey = `${params.card}_${params.action}__${params.item?.id}`;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [buttonKey]: "LOADING",
    },
  });

  const [actionAccessRes, actionAccessErr, actionAccessNet] =
    await DivisionAccessGrantAdminActionView.update({
      pk: params.item?.id,
      data: {
        action: "CONFIRM",
      },
      apiToken: controller.apiToken,
      extra: { division: controller.data.division },
    });

  if (actionAccessErr) {
    controller.setState({
      buttonLoadCheck: {
        ...controller.getState().buttonLoadCheck,
        [buttonKey]: "ERROR",
      },
    });
  } else {
    HandleSearch(controller, {
      action: "SEARCH",
      card: params.card,
      ownerActivePage: 1,
      granteeActivePage: 1,
    });

    controller.setState({
      buttonLoadCheck: {
        ...controller.getState().buttonLoadCheck,
        [buttonKey]: "SUCCESS",
      },
    });
  }
};
